import React from 'react'
import ContentLoader from 'react-content-loader'
import styled from '@emotion/styled'

import { Box } from '../Box/Box'
import { useScreenSize } from '../../hooks/useScreenSize'
import useResponsiveProp from '../../hooks/useResponsiveProp'

export interface LoaderProps {
  height: number
  loading: boolean
  children: React.ReactNode
  type?: 'table' | 'section' | 'cards'
  numOfCards?: number
}

export const Loader: React.FC<LoaderProps> = ({
  loading = true,
  height = 45,
  children,
  type = 'table',
  numOfCards = 1
}) => {
  const device = useScreenSize()
  const responsiveNumOfCards = useResponsiveProp(numOfCards, device)
  const responsiveHeight = useResponsiveProp(height, device)

  const tableLoader = () => {
    const rows = Math.ceil(responsiveHeight / 44 - 1)
    const rowsArray = []

    for (let i = 0; i < rows; i++) {
      rowsArray.push(
        <rect key={i} x="0" y={Number(44 * i) + 12} rx="4" ry="4" width="100%" height="20" />
      )
    }

    return (
      <ContentLoader height={responsiveHeight} style={{ width: '100%' }}>
        {rowsArray.map((row) => row)}
      </ContentLoader>
    )
  }

  const sectionLoader = () => {
    const rows = Math.ceil(responsiveHeight / 44 - 1)
    const rowsArray = []

    for (let i = 1; i <= rows; i++) {
      rowsArray.push(
        <rect key={i} x="0" y={Number(44 * i) + 12} rx="4" ry="4" width="100%" height="16" />
      )
    }

    return (
      <ContentLoader height={responsiveHeight} style={{ width: '100%' }}>
        <rect x="0" y="12" rx="4" ry="4" width="40%" height="20" />
        <rect x="80%" y="12" rx="4" ry="4" width="20%" height="20" />
        {rowsArray.map((row) => row)}
      </ContentLoader>
    )
  }

  const cardsLoader = () => {
    const cardsArray = []

    for (let i = 1; i <= responsiveNumOfCards; i++) {
      cardsArray.push(
        <ContentLoader height={responsiveHeight} style={{ width: '100%' }}>
          <rect x="0" y="0" rx="16" ry="16" width="100%" height="100%" />
        </ContentLoader>
      )
    }

    return (
      <GridBox height={responsiveHeight} numOfCards={responsiveNumOfCards}>
        {cardsArray.map((card) => card)}
      </GridBox>
    )
  }

  if (loading) {
    switch (type) {
      case 'table':
        return tableLoader()
      case 'cards':
        return cardsLoader()
      default:
        return sectionLoader()
    }
  } else if (children) {
    return <>{children}</>
  } else {
    return null
  }
}

interface GridBoxProps {
  numOfCards: string | number
}

const GridBox = styled(Box)<GridBoxProps>`
  display: grid;
  grid-gap: 16px;
  grid-template-columns: ${(p) => `repeat(${p.numOfCards}, 1fr)`};
`
